// ============================================================================
//  smartStableTheme — مصدر ألوان النظام + نظام مجموعات قابلة للتبديل (3.4.0).
// ----------------------------------------------------------------------------
//  يدعم النظام أكثر من "مجموعة ألوان" (هوية) لأنه قد يُركَّب لمواقع مختلفة:
//   • smartstable (الافتراضية): الهوية الجديدة — أخضر داكن + ذهبي + كريمي.
//   • nudrah: هوية موقع إسطبل ندرة الحالية — ذهبي + كريمي.
//  الاختيار يُحفظ في localStorage ويُطبَّق على كامل الواجهة. SMART_COLORS
//  يُحلّ من المجموعة الفعّالة عند تحميل الوحدة، فكل المستهلكين يحصلون عليها.
// ============================================================================

// هوية إسطبل ندرة الحالية (تبقى كما هي تمامًا).
const NUDRAH = {
  page: '#fbfaf7', card: '#fffdf8', cardSoft: '#f7f3ea', line: '#eadfca',
  text: '#4f545c', muted: '#7a7f87',
  gold: '#b79b5b', goldHover: '#a68847', goldSoft: '#efe4cb',
  blue: '#6f8fa6', blueSoft: '#eaf2f6',
  green: '#789b82', greenSoft: '#edf5ee',
  rose: '#b97878', roseSoft: '#f6ecea',
  warning: '#c29355', warningSoft: '#f7ead8',
  tableHead: '#c2ad74', tableHeadBorder: '#ad9253',
  // ألوان "الكروم" العامة (شريط علوي/قائمة/أزرار/ثيم MUI) — قيم ندرة الحالية كما هي.
  appbar: '#a68b4d', appbarHover: '#b49d6a', ink: '#58595b', paper: '#f5f2eb', surface: '#d5c6a9', sidebarAccent: '#252248',
};

// الهوية الجديدة الافتراضية — أخضر داكن + ذهبي + كريمي (من لوحة العلامة).
const SMARTSTABLE = {
  page: '#faf8f1', card: '#fffefb', cardSoft: '#f1ece0', line: '#e2dac6',
  text: '#21342c', muted: '#6f7d75',
  gold: '#a68b4d', goldHover: '#8f7740', goldSoft: '#ecdfc3',
  blue: '#6f8fa6', blueSoft: '#eaf2f6',
  green: '#1f4a3a', greenSoft: '#e3ece7',
  rose: '#b35b5b', roseSoft: '#f3e3e1',
  warning: '#c29355', warningSoft: '#f7ead8',
  tableHead: '#1f4a3a', tableHeadBorder: '#163a2d',
  // الكروم العام للهوية الجديدة — شريط/قائمة/أزرار خضراء داكنة + كريمي.
  appbar: '#1f4a3a', appbarHover: '#173d2f', ink: '#21342c', paper: '#f1ece0', surface: '#e7e0cf', sidebarAccent: '#163a2d',
};

// المجموعات المتاحة (swatch = ألوان المعاينة في المُنتقي، 5 كاللوحة المرفقة).
export const THEME_PALETTES = {
  // swatch = [المرساة الداكنة (القائمة الجانبية), الذهبي, السطح, الورق, الصفحة] — تعكس الهوية الحقيقية.
  smartstable: { key: 'smartstable', name_en: 'SmartStable', name_ar: 'سمارت ستيبل', colors: SMARTSTABLE, swatch: [SMARTSTABLE.appbar, SMARTSTABLE.gold, SMARTSTABLE.surface, SMARTSTABLE.paper, SMARTSTABLE.page] },
  nudrah: { key: 'nudrah', name_en: 'Nudrah', name_ar: 'ندرة', colors: NUDRAH, swatch: [NUDRAH.sidebarAccent, NUDRAH.appbar, NUDRAH.surface, NUDRAH.paper, NUDRAH.page] },
};

// الافتراضية = smartstable، وقابلة للضبط لكل تركيب عبر متغيّر بيئة وقت البناء
// (مثلًا REACT_APP_DEFAULT_THEME=nudrah لتركيب موقع ندرة) — مع إبقاء تجاوز المستخدم.
const ENV_DEFAULT = String(process.env.REACT_APP_DEFAULT_THEME || '').trim();
export const DEFAULT_PALETTE = (ENV_DEFAULT && THEME_PALETTES[ENV_DEFAULT]) ? ENV_DEFAULT : 'smartstable';
const STORAGE_KEY = 'smart_theme_palette';

export function getActivePaletteKey() {
  try {
    const k = localStorage.getItem(STORAGE_KEY);
    if (k && THEME_PALETTES[k]) return k;
  } catch (_) { /* localStorage غير متاح */ }
  return DEFAULT_PALETTE;
}

export function setActivePaletteKey(key) {
  if (!THEME_PALETTES[key]) return false;
  try { localStorage.setItem(STORAGE_KEY, key); } catch (_) { /* تجاهل */ }
  return true;
}

// الألوان الفعّالة — تُحلّ من المجموعة المختارة عند تحميل الوحدة.
export const SMART_COLORS = { ...THEME_PALETTES[getActivePaletteKey()].colors };

// جسر متغيّرات CSS: ينشر توكنات المجموعة كـ --smart-<token> على <html> ليستهلكها
// ملفّ App.css وأي CSS عام، فتتبع الهوية الفعّالة (قيم ندرة = القيم السابقة).
export function applyPaletteCssVars(key = getActivePaletteKey()) {
  if (typeof document === 'undefined') return;
  const colors = (THEME_PALETTES[key] || THEME_PALETTES[DEFAULT_PALETTE]).colors;
  const root = document.documentElement;
  for (const [k, v] of Object.entries(colors)) root.style.setProperty(`--smart-${k}`, v);
}

// تطبيق مبكّر عند تحميل الوحدة (قبل أوّل رسم) — <html> متاح في المتصفّح.
applyPaletteCssVars();

export const smartCardSx = {
  border: `1px solid ${SMART_COLORS.line}`,
  borderRadius: 2,
  background: SMART_COLORS.card,
  boxShadow: '0 8px 22px rgba(91, 75, 39, 0.06)',
};

export const smartPageSx = {
  minHeight: '100vh',
  bgcolor: SMART_COLORS.page,
  color: SMART_COLORS.text,
};

export const smartTableHeadSx = {
  borderRadius: 0,
  '& .MuiTableRow-head': {
    bgcolor: SMART_COLORS.tableHead,
    borderRadius: 0,
    height: 48,
  },
  '& .MuiTableCell-root': {
    bgcolor: SMART_COLORS.tableHead,
    color: '#fff',
    fontWeight: 900,
    borderTop: 0,
    borderBottom: `2px solid ${SMART_COLORS.tableHeadBorder}`,
    borderRadius: '0 !important',
    py: 1.25,
    lineHeight: 1.25,
    letterSpacing: 0,
    whiteSpace: 'nowrap',
  },
};

export const smartTableContainerSx = {
  borderRadius: 0,
  overflowX: 'auto',
};

export const smartTableSx = {
  borderCollapse: 'collapse',
  borderRadius: 0,
};

export const smartActionButtonSx = (color, bg, hoverBg) => ({
  width: 34,
  height: 34,
  color,
  bgcolor: bg,
  border: `1px solid ${bg}`,
  '&:hover': {
    bgcolor: hoverBg || bg,
    borderColor: color,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
});
